import { isDroneTabHidden, visibleDroneTab } from '@/lib/droneContextFlags';
import { useUiStore, type TabId } from '@/lib/store/ui';
import { cn } from '@/lib/utils';

interface Tab {
  id: TabId;
  label: string;
}

// Greedy row-fill behaviour: render every tab in a single `flex-wrap`
// container and let CSS decide how many fit per row at the current panel
// width. No fixed primary/secondary split — tabs just flow.
//   wide:   one row, every visible tab
//   medium: two rows (CSS picks where to break)
//   narrow: 3+ rows
// Earlier two-row layout matched the mockup's _rebalanceTabs visual but
// hid tabs behind `overflow-x-auto` at narrow widths — bug reported.
const TABS: Tab[] = [
  { id: 'ldt-status', label: 'Overview' },
  { id: 'ldt-telemetry', label: 'Telemetry' },
  { id: 'ldt-daa', label: 'Surveillance' },
  { id: 'ldt-c2', label: 'C2' },
  { id: 'ldt-log', label: 'Events' },
  { id: 'ldt-proximity', label: 'Proximity' },
  { id: 'ldt-agent', label: 'Agents' },
  { id: 'ldt-terrain', label: 'Terrain' },
  { id: 'ldt-flight', label: 'Flight Ops' },
  { id: 'ldt-weather', label: 'Weather' },
];

export function TabStrip() {
  const active = visibleDroneTab(useUiStore((s) => s.activeTab));
  const setTab = useUiStore((s) => s.setTab);

  return (
    <nav className="shrink-0 border-b border-(--color-border) bg-(--color-surface)">
      <div className="flex flex-wrap">
        {/* ENG-271: tabs listed in HIDDEN_DRONE_TABS are skipped, not removed. */}
        {TABS.filter((t) => !isDroneTabHidden(t.id)).map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={cn(
              'shrink-0 px-3 py-1.5 text-[0.769em] font-semibold whitespace-nowrap border-b-2 transition-colors',
              t.id === active
                ? 'border-(--color-green) text-(--color-green)'
                : 'border-transparent text-(--color-text2) hover:text-(--color-text)',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>
    </nav>
  );
}
